import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  TimePicker,
  message,
  Alert,
  Spin,
  Breadcrumb,
  Typography,
} from "antd";
import {
  LoadingOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import CustomSimpleReactValidator from "../../../components/CustomSimpleReactValidator";
import UserServices from "../../../services/UserServices";
import { get_language, translate } from "../../../locale";
import Paragraph from "antd/lib/typography/Paragraph";

const arrayDays = [
  { id: 0, label: "Domingo" },
  { id: 1, label: "Lunes" },
  { id: 2, label: "Martes" },
  { id: 3, label: "Miércoles" },
  { id: 4, label: "Jueves" },
  { id: 5, label: "Viernes" },
  { id: 6, label: "Sábado" },
];

const { Title } = Typography;

const NuevoConsumo = () => {
  const params = useParams();
  const [medicine, setMedicine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [values, setValues] = useState({
    medicine_id: null,
    patient: null,
    days: [0, 1, 2, 3, 4, 5, 6],
    time: [],
    date_from: moment().format("YYYY-MM-DD"),
    date_to: "",
    dosage: "",
    takingDaily: 1,
    firstTakingHour: 0,
    firstTakingHourDirty: false,
  });
  const [, forceUpdate] = useState();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const simpleValidator = useRef(
    CustomSimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
    })
  );

  const disabled = () =>
    !simpleValidator.current.allValid() || !values.firstTakingHourDirty;

  useEffect(() => {
    const _medicine = JSON.parse(localStorage.getItem("medicine"));
    setMedicine(_medicine);
    setValues((v) => ({
      ...v,
      patient: params.userName,
      medicine_id: params.medicineId,
    }));
  }, [params.medicineId, params.userName]);

  const handleChangeCheckbox = (name, checkedValues) => {
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: checkedValues }));
  };

  const handleChangeDatepicker = (name, value) => {
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({
      ...v,
      [name]: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const addRowTime = (time) => {
    setValues((v) => ({
      ...v,
      time: [...v.time, time ?? "00:00"],
    }));
    simpleValidator.current.showMessageFor("time");
  };

  const onFinish = async (e) => {
    let body = values;

    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los campos son requeridos.");
      return;
    }

    if (e.time) {
      body = {
        ...body,
        time: e.time.map((i) => i.hour.format("HH:mm")),
        dosage: e.time.map((i) => i.dosage.toFixed(2)),
      };
    }

    try {
      const res = await UserServices.assignMedicine(body);

      if (res) {
        if (res.status === 400) {
          setError(res.data.messaje);
        } else if (res.status >= 200 && res.status < 300) {
          message.success("La medicina se agregó exitosamente.");
          resetForm();
          navigate(-2, { state: { reload: true } });
        } else {
          message.success(res.data.messaje, 3);
        }
        setLoading(false);
      } else {
        setError("Log in error");
      }
    } catch (e) {
      console.log(e);
      setError(e.messaje);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues((v) => ({
      ...v,
      days: [1, 2, 3, 4, 5, 6, 7],
      time: [],
      date_from: moment().format("YYYY-MM-DD"),
      date_to: "",
      dosage: "",
      firstTakingHourDirty: false,
    }));
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const handleSelectTime = (moment, index) => {
    const curTimes = form.getFieldsValue("time");

    const updatedtimes = curTimes.time.map((curTime, k) => {
      if (k === index) {
        return {
          ...curTime,
          hour: moment,
        };
      }

      return curTime;
    });
    form.setFieldsValue({
      time: updatedtimes,
    });
    setValues((v) => ({
      ...v,
      time: updatedtimes.map((i) => i.hour.format("HH:mm")),
    }));
  };

  const calculateHours = (takingDaily, firstTakingHour) => {
    if (takingDaily <= 0) {
      return [];
    }

    const interval = Math.floor(24 / takingDaily);
    const hours = [];

    for (let i = 0; i < takingDaily; i++) {
      const hour = (firstTakingHour + i * interval) % 24;
      const formattedHour = `${hour.toString().padStart(2, "0")}:00`; // Formato HH:mm
      hours.push(formattedHour);
    }

    return hours;
  };

  const addInputHours = (
    fields,
    add,
    remove,
    takingDaily = 1,
    firstTakingHour = 0
  ) => {
    remove(fields.map((field) => field.name));
    setValues((v) => ({
      ...v,
      time: [],
    }));
    const newFields = calculateHours(takingDaily, firstTakingHour);
    newFields.forEach((i) => {
      add({
        hour: moment(i, "HH:mm"),
        dosage: 1.0,
      });
      addRowTime(i);
    });
  };

  return (
    <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 m-10">
      <Row>
        <Col className="gutter-row" span="24">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/admin">{translate("Inicio")}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/admin/buscar-paciente`}>
                {translate("Resumen individual")}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link
                to={`/admin/buscar-paciente/${params.userName}/medicinas-paciente`}
              >
                {translate("Medicamentos del Paciente")}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {translate("Nuevo Consumo de Medicamento")}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col span="24" className="my-4">
          <Title level={2} className="text-center" style={{ marginBottom: 0 }}>
            {translate("Nuevo Consumo de Medicamento")}
          </Title>
        </Col>
      </Row>
      <Form form={form} onFinish={onFinish} autoComplete="off">
        <Row gutter={16}>
          <Col span="24" className="mb-4">
            {medicine && (
              <Card className="w-full">
                <span className="font-bold">{translate("Monodroga:")}</span>{" "}
                {medicine.monodrug ? medicine.monodrug : "-"}
                <br />
                <span className="font-bold">
                  {translate("Marca Comercial:")}
                </span>{" "}
                {medicine.name ? medicine.name : "-"}
                <br />
                <span className="font-bold">
                  {translate("Presentación:")}
                </span>{" "}
                {medicine.presentation ? medicine.presentation : "-"}
                <br />
                <span className="font-bold">{translate("Potencia:")}</span>{" "}
                {medicine.power ? medicine.power : "-"}
              </Card>
            )}
          </Col>
          <Col span="8">
            <Card className="w-full mb-4">
              <Row className="mb-4">
                <Col>{translate("¿Qué días toma el medicamento?")}</Col>
              </Row>

              <Checkbox.Group
                name="days"
                onChange={(e) => {
                  handleChangeCheckbox("days", e);
                }}
                defaultValue={values.days}
              >
                {arrayDays.map((i) => (
                  <Row key={i.id}>
                    <Col>
                      <Checkbox value={i.id}>{i.label}</Checkbox>
                    </Col>
                  </Row>
                ))}
              </Checkbox.Group>

              {simpleValidator.current.message(
                "days",
                values.days,
                "required",
                get_language === "es"
                  ? {
                      messages: {
                        required: "Debe seleccionar un día de la semana",
                      },
                    }
                  : {}
              )}
            </Card>
          </Col>
          <Col span="16">
            <Card className="w-full mb-4">
              <Row>
                <Col span="12">
                  <Row className="mb-4">
                    <Col>{translate("¿Cuándo comienza a tomar?")}</Col>
                  </Row>
                  <Row>
                    <Col>
                      <DatePicker
                        name="date_from"
                        defaultValue={moment()}
                        format={"DD/MM/YYYY"}
                        onChange={(e) => {
                          handleChangeDatepicker("date_from", e);
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span="12">
                  <Row className="mb-4">
                    <Col>
                      {translate(
                        "¿Cuándo finaliza? (dejar en blanco si es continua)"
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <DatePicker
                        name="date_to"
                        format={"DD/MM/YYYY"}
                        onChange={(e) => {
                          handleChangeDatepicker("date_to", e);
                        }}
                        disabledDate={(d) =>
                          !d || d.isBefore(moment().format("YYYY-MM-DD"))
                        }
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
            <Card className="w-full">
              <Form.List name="time">
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <Row className="mb-4">
                        <Col span={12}>
                          <Paragraph>{translate("Tomas diarias")}</Paragraph>
                          <InputNumber
                            defaultValue={1}
                            min={1}
                            max={24}
                            onChange={(takingDaily) => {
                              setValues((v) => ({
                                ...v,
                                takingDaily,
                              }));
                              addInputHours(
                                fields,
                                add,
                                remove,
                                takingDaily,
                                values.firstTakingHour
                              );
                            }}
                          />
                        </Col>
                        <Col span={12}>
                          <Paragraph>
                            {translate("Hora de la primera dosis")}
                          </Paragraph>
                          <TimePicker
                            format="HH:mm"
                            minuteStep={1}
                            onChange={(firstTakingHour) => {
                              setValues((v) => ({
                                ...v,
                                firstTakingHour: firstTakingHour?.hour() ?? 0,
                                firstTakingHourDirty: true,
                              }));
                              addInputHours(
                                fields,
                                add,
                                remove,
                                values.takingDaily,
                                firstTakingHour?.hour() ?? 0
                              );
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={24}>
                          <table style={{ tableLayout: "auto", width: "100%" }}>
                            <thead className="ant-table-thead">
                              <tr>
                                <th className="ant-table-cell">
                                  {translate("Hora")}
                                </th>
                                <th className="ant-table-cell">
                                  {translate("Dosis")}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="ant-table-tbody">
                              {fields.map((field, index) => (
                                <tr
                                  className="ant-table-row ant-table-row-level-0"
                                  key={field.key}
                                >
                                  <td className="ant-table-cell">
                                    <Form.Item
                                      name={[field.name, "hour"]}
                                      rules={[{ required: true }]}
                                      className="mb-0"
                                      initialValue={moment("00:00", "HH:mm")}
                                    >
                                      <TimePicker
                                        name="timehour"
                                        hourStep={1}
                                        minuteStep={30}
                                        format="HH:mm"
                                        onSelect={(e) =>
                                          handleSelectTime(e, index)
                                        }
                                      />
                                    </Form.Item>
                                  </td>
                                  <td className="ant-table-cell">
                                    <Form.Item
                                      name={[field.name, "dosage"]}
                                      rules={[{ required: true }]}
                                      className="mb-0"
                                      initialValue={1.0}
                                    >
                                      <InputNumber
                                        decimalSeparator=","
                                        step={0.25}
                                        precision={2}
                                        min={0.25}
                                      />
                                    </Form.Item>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </Form.List>
            </Card>
            <Row gutter={16} className="mt-6" justify="end">
              {error && (
                <Alert
                  message={error}
                  type="error"
                  className="mb-6"
                  closable
                  onClose={() => setError("")}
                />
              )}
              <Col>
                <Button icon={<CloseOutlined />}>
                  {translate("Cancelar")}
                </Button>
              </Col>
              <Col>
                <Button
                  disabled={disabled()}
                  icon={<SaveOutlined />}
                  type={disabled() ? "default" : "success"}
                  htmlType="submit"
                >
                  {loading && (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  )}
                  {translate("Guardar")}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default NuevoConsumo;
